import { recursivelyValidateAndTransformDateToString } from "../../utils";
import { axios, dateOnlyTransformer } from "../axios";
import { BaseResponseWithSingle, Status } from "../Shared";
import {
  ApproveOrRejectDealRequest,
  CreateDealRequest,
  CreateDealResponse,
  DeleteDealDocumentRequest,
  DownloadDealDocumentRequest,
  UpdateDealRequest,
  UpdateDealResponse,
  UploadDealDocumentRequest,
  UploadDealDocumentResponse,
} from "./Types";

const transformDealRequestDates = (
  req: CreateDealRequest | UpdateDealRequest
): { data: CreateDealRequest | UpdateDealRequest | null; errors: BaseResponseWithSingle<null>["errors"] } => {
  const transformedDealDates = recursivelyValidateAndTransformDateToString(req.dealDates);
  const transformedDealSpecificData = recursivelyValidateAndTransformDateToString(req.dealSpecificData);
  const errors = [...transformedDealDates.errors, ...transformedDealSpecificData.errors];

  if (errors.length) {
    return {
      data: null,
      errors,
    };
  }

  return {
    data: {
      ...req,
      dealDates: transformedDealDates.data,
      dealSpecificData: transformedDealSpecificData.data,
      introductionDate: dateOnlyTransformer(req.introductionDate) as Date | null,
      targetCloseDate: dateOnlyTransformer(req.targetCloseDate) as Date | null,
    },
    errors,
  };
};

export const createDeal = async (req: CreateDealRequest): Promise<BaseResponseWithSingle<CreateDealResponse>> => {
  const transformedRequest = transformDealRequestDates(req);

  if (transformedRequest.errors?.length) {
    return {
      status: Status.Error,
      errors: transformedRequest.errors || [],
    };
  }

  return axios
    .request<BaseResponseWithSingle<CreateDealResponse>>({
      method: "post",
      url: "investment/deal",
      data: transformedRequest.data,
    })
    .then((res) => {
      return {
        status: Status.Success,
        data: res.data.data,
      };
    })
    .catch((error) => {
      return {
        status: Status.Error,
        errors: error.errors || [],
      };
    });
};

export const approveOrRejectDeal = async (
  req: ApproveOrRejectDealRequest
): Promise<BaseResponseWithSingle<UpdateDealResponse>> => {
  return axios
    .request<BaseResponseWithSingle<UpdateDealResponse>>({
      method: "patch",
      url: "investment/deal",
      data: req,
    })
    .then((res) => {
      return {
        status: Status.Success,
        data: res.data.data,
      };
    })
    .catch((error) => {
      return {
        status: Status.Error,
        errors: error.errors || [],
      };
    });
};

export const updateDeal = async (req: UpdateDealRequest): Promise<BaseResponseWithSingle<UpdateDealResponse>> => {
  const transformedRequest = transformDealRequestDates(req);

  if (transformedRequest.errors?.length) {
    return {
      status: Status.Error,
      errors: transformedRequest.errors || [],
    };
  }

  return axios
    .request<BaseResponseWithSingle<UpdateDealResponse>>({
      method: "put",
      url: "investment/deal",
      data: transformedRequest.data,
    })
    .then((res) => {
      return {
        status: Status.Success,
        data: res.data.data,
      };
    })
    .catch((error) => {
      return {
        status: Status.Error,
        errors: error.errors || [],
      };
    });
};

export const DeleteDealDocument = async (req: DeleteDealDocumentRequest): Promise<BaseResponseWithSingle<null>> => {
  return axios
    .request<BaseResponseWithSingle<null>>({
      method: "delete",
      url: "investment/deal/document",
      data: req,
    })
    .then(() => {
      return {
        status: Status.Success,
      };
    })
    .catch((error) => {
      return {
        status: Status.Error,
        errors: error.errors || [],
      };
    });
};

export const deleteDealDocument = async (req: DeleteDealDocumentRequest): Promise<BaseResponseWithSingle<null>> => {
  return DeleteDealDocument(req);
};

export const uploadDealDocument = async (
  req: UploadDealDocumentRequest
): Promise<BaseResponseWithSingle<UploadDealDocumentResponse>> => {
  const formData = new FormData();

  formData.append("CustomVariant", req.customVariant);
  formData.append("DealDocumentUuid", req.dealDocumentUuid);
  formData.append("DealUuid", req.dealUuid);
  formData.append("DocumentType", req.documentType);

  for (let i = 0; i < req.files.length; i++) {
    formData.append("Files", req.files[i], req.files[i].name);
  }

  return axios
    .request<BaseResponseWithSingle<UploadDealDocumentResponse>>({
      method: "post",
      url: "investment/deal/document",
      data: formData,
      timeout: 120000,
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => {
      return {
        status: Status.Success,
        data: res.data.data,
      };
    })
    .catch((error) => {
      return {
        status: Status.Error,
        errors: error.errors || [],
      };
    });
};

export const downloadDealDocument = async (
  req: DownloadDealDocumentRequest
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<BaseResponseWithSingle<any>> => {
  const url = `investment/deal/document/download?DealDocumentHistoryUuid=${req.dealDocumentHistoryUuid}`;
  return axios
    .request({
      method: "get",
      url,
      data: req,
      responseType: "blob",
      timeout: 120000,
    })
    .then((response) => {
      return {
        status: Status.Success,
        data: response,
      };
    })
    .catch((error) => {
      return {
        status: Status.Error,
        errors: error?.errors || [],
      };
    });
};
